import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

// UOM values the Received Qty label can render. Nothing here is hard-coded to BOX —
// the label always follows whichever UOM the row is set to.
const UOMS = ['PCS', 'BOX', 'BIN', 'KGS', 'LTR', 'MTR', 'ROLL', 'SET', 'PALLET'];

const emptyItem = { partNo: '', partName: '', totalQuantity: '', uom: 'PCS', boxes: [], okQty: '', damagedQty: '', missingQty: '', cause: '' };
const emptyBox  = { boxNumber: '', boxQuantity: '' };

const GRNForm = () => {
  const navigate = useNavigate();
  const [inwards, setInwards] = useState([]);
  const [selectedInward, setSelectedInward] = useState(null);
  const [inwardId, setInwardId] = useState('');
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [extra, setExtra] = useState({
    materialTestCertNo: '', ewayBillNo: '', materialLocation: '',
    materialCountedBy: '', remarks: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/inward').then((r) => setInwards(r.data.data)).catch(console.error);
  }, []);

  const handleInwardChange = (id) => {
    setInwardId(id);
    const found = inwards.find((i) => i._id === id);
    setSelectedInward(found || null);
    if (found) {
      setItems(
        (found.items || []).map((it) => ({
          partNo: it.product?.partNumber || it.product?.projectName || it.product?.sku || '',
          partName: it.itemName || '',
          totalQuantity: it.quantity || '',
          // Carry the UOM through from the inward line so the Received Qty label
          // starts out matching what was actually received.
          uom: (it.unit || 'PCS').toUpperCase(),
          boxes: [],
          // Default to "all OK" — the common case — so Total = OK + Damaged + Missing
          // reconciles automatically unless the user records a discrepancy below.
          okQty: it.quantity || '', damagedQty: '', missingQty: '', cause: '',
        }))
      );
    }
  };

  const updateItem = (i, field, val) => {
    const updated = [...items];
    const next = { ...updated[i], [field]: val };
    // Keep OK Qty reconciled automatically: OK = Total - Damaged - Missing.
    if (field === 'totalQuantity' || field === 'damagedQty' || field === 'missingQty') {
      const total = Number(field === 'totalQuantity' ? val : next.totalQuantity) || 0;
      const damaged = Number(field === 'damagedQty' ? val : next.damagedQty) || 0;
      const missing = Number(field === 'missingQty' ? val : next.missingQty) || 0;
      next.okQty = String(Math.max(0, total - damaged - missing));
    }
    updated[i] = next;
    setItems(updated);
  };

  const addBox = (i) => {
    const updated = [...items];
    updated[i] = { ...updated[i], boxes: [...updated[i].boxes, { ...emptyBox }] };
    setItems(updated);
  };

  const updateBox = (itemIdx, boxIdx, field, val) => {
    const updated = [...items];
    const boxes = [...updated[itemIdx].boxes];
    boxes[boxIdx] = { ...boxes[boxIdx], [field]: val };
    updated[itemIdx] = { ...updated[itemIdx], boxes };
    setItems(updated);
  };

  const removeBox = (itemIdx, boxIdx) => {
    const updated = [...items];
    updated[itemIdx].boxes = updated[itemIdx].boxes.filter((_, idx) => idx !== boxIdx);
    setItems(updated);
  };

  const handleSubmit = async () => {
    if (!inwardId) return toast.error('Please select an Inward Receipt');
    if (items.some((i) => !i.partName || !i.totalQuantity))
      return toast.error('Please fill Part Name and Quantity for all items');

    setLoading(true);
    try {
      const res = await api.post('/grn', {
        inwardReceiptId: inwardId,
        items: items.map((i) => ({
          partNo: i.partNo,
          partName: i.partName,
          totalQuantity: Number(i.totalQuantity),
          uom: i.uom || 'PCS',
          boxes: i.boxes.map((b) => ({ boxNumber: b.boxNumber, boxQuantity: Number(b.boxQuantity) || 0 })),
          okQty: Number(i.okQty) || 0,
          damagedQty: Number(i.damagedQty) || 0,
          missingQty: Number(i.missingQty) || 0,
          cause: i.cause || '',
        })),
        ...extra,
      });
      const grnId = res.data.data._id;
      await api.patch(`/grn/${grnId}/generate`);
      toast.success('GRN generated successfully!');
      navigate('/grn');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create GRN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Inward selection */}
      <div className="zip-card p-6 space-y-4">
        <h3 className="font-semibold text-zip-text tracking-tight">Select Inward Receipt</h3>
        <div>
          <label className="block text-xs font-medium text-zip-muted mb-1.5">Inward No. *</label>
          <select className="zip-input" value={inwardId} onChange={(e) => handleInwardChange(e.target.value)}>
            <option value="">Select Inward Receipt…</option>
            {inwards.map((i) => (
              <option key={i._id} value={i._id}>
                {i.inwardNumber} — {i.supplier?.name} ({i.invoiceNumber})
              </option>
            ))}
          </select>
        </div>
        {selectedInward && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs">
            <div><span className="text-zip-muted">Inward No.</span><br /><span className="font-mono font-semibold text-amber-700">{selectedInward.inwardNumber}</span></div>
            <div><span className="text-zip-muted">Supplier</span><br /><span className="font-medium">{selectedInward.supplier?.name}</span></div>
            <div><span className="text-zip-muted">Invoice No.</span><br /><span className="font-mono">{selectedInward.invoiceNumber}</span></div>
            <div><span className="text-zip-muted">Items</span><br /><span className="font-mono">{selectedInward.totalItems}</span></div>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="zip-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zip-text tracking-tight">GRN Items</h3>
          <button
            type="button"
            onClick={() => setItems([...items, { ...emptyItem }])}
            className="btn-ghost flex items-center gap-1.5 text-xs"
          >
            <Plus size={13} /> Add Item
          </button>
        </div>

        {items.map((item, i) => (
          <div key={i} className="border border-zip-border rounded-xl p-4 space-y-3 bg-zip-surface">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs text-zip-muted mb-1">Part No.</label>
                <input type="text" className="zip-input text-sm" value={item.partNo}
                  onChange={(e) => updateItem(i, 'partNo', e.target.value)} placeholder="PN-001" />
              </div>
              <div>
                <label className="block text-xs text-zip-muted mb-1">Part Name / Description *</label>
                <input type="text" className="zip-input text-sm" value={item.partName}
                  onChange={(e) => updateItem(i, 'partName', e.target.value)} placeholder="e.g. Cardboard Box" />
              </div>
              <div>
                <label className="block text-xs text-zip-muted mb-1">UOM</label>
                <select className="zip-input text-sm" value={item.uom}
                  onChange={(e) => updateItem(i, 'uom', e.target.value)}>
                  {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  {/* Label follows the selected UOM — never hard-coded to BOX. */}
                  <label className="block text-xs text-zip-muted mb-1">
                    Received Qty ({item.uom || 'PCS'}) *
                  </label>
                  <input type="number" min="0" className="zip-input text-sm" value={item.totalQuantity}
                    onChange={(e) => updateItem(i, 'totalQuantity', e.target.value)} placeholder="0" />
                </div>
                {items.length > 1 && (
                  <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                    className="text-zip-muted hover:text-zip-red mb-0.5"><Trash2 size={14} /></button>
                )}
              </div>
            </div>

            {/* OK / Damaged / Missing / Cause */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-zip-muted mb-1">OK Qty</label>
                <input type="number" min="0" className="zip-input text-sm" value={item.okQty}
                  onChange={(e) => updateItem(i, 'okQty', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className="block text-xs text-zip-muted mb-1">Damaged Qty</label>
                <input type="number" min="0" className="zip-input text-sm" value={item.damagedQty}
                  onChange={(e) => updateItem(i, 'damagedQty', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className="block text-xs text-zip-muted mb-1">Missing Qty</label>
                <input type="number" min="0" className="zip-input text-sm" value={item.missingQty}
                  onChange={(e) => updateItem(i, 'missingQty', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className="block text-xs text-zip-muted mb-1">Cause</label>
                <input type="text" className="zip-input text-sm" value={item.cause}
                  onChange={(e) => updateItem(i, 'cause', e.target.value)} placeholder="e.g. Transit damage" />
              </div>
            </div>

            {/* Box details sub-tab */}
            <div className="pl-1 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-zip-muted font-medium">Box Details</span>
                <button type="button" onClick={() => addBox(i)}
                  className="text-xs text-zip-red hover:underline flex items-center gap-1">
                  <Plus size={11} /> Add Box
                </button>
              </div>
              {item.boxes.map((box, bi) => (
                <div key={bi} className="flex gap-2 items-center">
                  <input type="text" className="zip-input text-xs flex-1" value={box.boxNumber}
                    onChange={(e) => updateBox(i, bi, 'boxNumber', e.target.value)} placeholder="Box No." />
                  <input type="number" min="0" className="zip-input text-xs w-24" value={box.boxQuantity}
                    onChange={(e) => updateBox(i, bi, 'boxQuantity', e.target.value)} placeholder="Qty" />
                  <button type="button" onClick={() => removeBox(i, bi)}
                    className="text-zip-muted hover:text-zip-red"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Non-mandatory fields */}
      <div className="zip-card p-6 space-y-4">
        <h3 className="font-semibold text-zip-text tracking-tight">Additional Details <span className="text-zip-muted font-normal text-xs">(optional)</span></h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ['materialTestCertNo',  'Material Test Certificate No.', 'MTC-001'],
            ['ewayBillNo',          'Eway Bill No.',                 'EWB-001'],
            ['materialLocation',    'Material Location',             'Rack A / Bay 3'],
            ['materialCountedBy',   'Material Counted By',          'John Doe'],
          ].map(([field, label, ph]) => (
            <div key={field}>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">{label}</label>
              <input type="text" className="zip-input" placeholder={ph}
                value={extra[field]} onChange={(e) => setExtra({ ...extra, [field]: e.target.value })} />
            </div>
          ))}
        </div>
        <div>
          <label className="block text-xs font-medium text-zip-muted mb-1.5">Remarks</label>
          <textarea className="zip-input min-h-[80px] resize-none" placeholder="Any additional remarks…"
            value={extra.remarks} onChange={(e) => setExtra({ ...extra, remarks: e.target.value })} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button type="button" onClick={handleSubmit} disabled={loading}
          className="btn-primary flex items-center gap-2">
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Generating…</>
          ) : 'Generate GRN'}
        </button>
        <button type="button" onClick={() => navigate('/grn')} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
};

export default GRNForm;
